import cx from 'classnames';
import { FC, ReactElement, cloneElement, DetailedHTMLProps, ButtonHTMLAttributes, forwardRef } from 'react';

export interface IButtonProps extends DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
  isLoading?: boolean;
  prefixIcon?: ReactElement;
  suffixIcon?: ReactElement;
  icon?: ReactElement;
  variant?: TButtonVariant;
  block?: boolean; // Makes button fit to parent width
  size?: TButtonSize;
  bold?: boolean;
  fontSize?: 'small' | 'medium';
}

export type TButtonSize = 'medium' | 'xx-large' | 'small' | 'xsmall';
export type TButtonFontSize = 'small' | 'medium';

export type TButtonVariant =
  | 'default'
  | 'primary'
  | 'destructive'
  | 'text'
  | 'gray'
  | 'link'
  | 'outlined'
  | 'raiderio-gradient';

type TIconPosition = 'prefix' | 'suffix';

const PRIOR_ICON_POSITION: TIconPosition = 'suffix' as TIconPosition;

export const Button: FC<IButtonProps> = forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      children,
      className,
      variant = 'default',
      prefixIcon: propPrefixIcon,
      suffixIcon: propSuffixIcon,
      icon: propIcon,
      type = 'button',
      isLoading = false,
      disabled,
      block = false,
      size = 'medium',
      bold = false,
      fontSize = 'small',
      ...restProps
    },
    ref
  ) => {
    const prefixIcon = PRIOR_ICON_POSITION === 'prefix' ? propPrefixIcon || propIcon : propPrefixIcon;
    const suffixIcon = PRIOR_ICON_POSITION === 'suffix' ? propSuffixIcon || propIcon : propSuffixIcon;

    const onlyIcon = !children && (prefixIcon || suffixIcon);
    const gapSize = size === 'xsmall' ? 'xx-small' : 'x-small';

    const variantClassNames: Record<TButtonVariant, string> = {
      primary: 'slds-button--brand rio-button--primary',
      default: 'slds-button--neutral rio-button--default',
      destructive: 'slds-button--destructive',
      text: 'rio-button--text',
      gray: 'rio-button--gray',
      link: 'rio-button--link',
      outlined: 'rio-button--outlined',
      'raiderio-gradient': 'slds-button--raiderio-gradient'
    };

    const sizeClassNames: Record<TButtonSize, string> = {
      medium: 'rio-button--medium',
      'xx-large': 'rio-button--large',
      small: 'rio-button--small',
      xsmall: 'rio-button--xsmall'
    };

    const fontSizeClassNames: Partial<Record<TButtonFontSize, string>> = {
      medium: 'rio-button--font-size-medium'
    };

    const buttonClassNames = cx(
      'slds-button',
      className,
      variantClassNames[variant],
      sizeClassNames[size],
      fontSizeClassNames[fontSize],
      {
        'rio-full-width': block,
        'text-bold': bold,
        'rio-button-with-icon': prefixIcon || suffixIcon,
        'rio-button--only-icon': onlyIcon
      }
    );

    const isIconMissing = !prefixIcon && !suffixIcon;

    return (
      <button {...restProps} disabled={isLoading || disabled} type={type} className={buttonClassNames} ref={ref}>
        <IconWrapper
          icon={prefixIcon}
          isLoading={isLoading && (isIconMissing ? false : !suffixIcon)}
          gapClass={!onlyIcon ? `slds-m-right--${gapSize}` : null}
        />
        {children}
        <IconWrapper
          icon={suffixIcon}
          isLoading={isLoading && (isIconMissing ? true : !prefixIcon)}
          gapClass={!onlyIcon ? `slds-m-left--${gapSize}` : null}
        />
      </button>
    );
  }
);

Button.displayName = 'Button';

const IconWrapper: FC<{
  icon: ReactElement | undefined;
  isLoading: boolean;
  gapClass: string | null;
}> = ({ icon, isLoading, gapClass }) => {
  if (isLoading) {
    return <i className={cx('fa fa-spinner fa-spin', gapClass)} />;
  }
  if (!icon) {
    return null;
  }
  const iconProps = icon.props as { className?: string };
  return cloneElement(icon as ReactElement<{ className?: string }>, { className: cx(iconProps?.className, gapClass) });
};
